import { useEffect, useMemo, useState } from 'react';
import { qiblaBearing, useGeolocation } from '../lib/geo';
import {
    METHODS, PRAYERS, formatCountdown, formatTime, prayerTimes,
    type Madhab, type PrayerName,
} from '../lib/prayerTimes';
import { useT } from '../i18n';
import './Prayer.css';

const METHOD_KEY = 'hy_prayer_method';
const MADHAB_KEY = 'hy_prayer_madhab';

/**
 * Prayer times and Qibla, on one screen.
 *
 * Times are computed on device rather than fetched. A traveller needs them
 * exactly when they have no connection — on a plane, on landing, on a foreign
 * SIM — and v2's API dependency meant the feature simply stopped working then.
 */
export function Prayer() {
    const { t } = useT();
    const position = useGeolocation();
    const [now, setNow] = useState(() => new Date());
    const [methodId, setMethodId] = useState(() => localStorage.getItem(METHOD_KEY) ?? 'MWL');
    const [madhab, setMadhab] = useState<Madhab>(() => (localStorage.getItem(MADHAB_KEY) as Madhab) ?? 'standard');
    const [heading, setHeading] = useState<number | null>(null);
    const [compass, setCompass] = useState<'idle' | 'granted' | 'denied' | 'unsupported'>('idle');

    // Tick once a minute so the countdown and the "next" highlight stay honest.
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 30_000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => { localStorage.setItem(METHOD_KEY, methodId); }, [methodId]);
    useEffect(() => { localStorage.setItem(MADHAB_KEY, madhab); }, [madhab]);

    const method = METHODS.find((m) => m.id === methodId) ?? METHODS[0]!;
    const result = useMemo(
        () => (position ? prayerTimes(position.lat, position.lng, now, method, madhab) : null),
        [position, now, method, madhab],
    );
    const bearing = position ? qiblaBearing(position.lat, position.lng) : null;

    // --- compass -------------------------------------------------------------
    useEffect(() => {
        const DOE = window.DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> } | undefined;
        if (!DOE) { setCompass('unsupported'); return; }
        if (typeof DOE.requestPermission !== 'function') setCompass('granted');
    }, []);

    useEffect(() => {
        if (compass !== 'granted') return;
        const onOrient = (e: DeviceOrientationEvent & { webkitCompassHeading?: number }) => {
            const h = e.webkitCompassHeading ?? (e.alpha != null ? 360 - e.alpha : null);
            if (h != null && Number.isFinite(h)) setHeading(h);
        };
        window.addEventListener('deviceorientation', onOrient as EventListener);
        return () => window.removeEventListener('deviceorientation', onOrient as EventListener);
    }, [compass]);

    const requestCompass = async () => {
        const DOE = window.DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> };
        try { setCompass((await DOE.requestPermission?.()) === 'granted' ? 'granted' : 'denied'); }
        catch { setCompass('denied'); }
    };

    const live = heading != null;
    const needle = bearing == null ? 0 : live ? bearing - heading! : bearing;

    if (!position) {
        return (
            <div className="prayer">
                <h1 className="prayer__title">{t('prayer.title')}</h1>
                <p className="prayer__msg">{t('prayer.needLocation')}</p>
            </div>
        );
    }

    return (
        <div className="prayer">
            <h1 className="prayer__title">{t('prayer.title')}</h1>

            {result?.next && (
                <div className="next">
                    <p className="next__label">{t('prayer.next')}</p>
                    <p className="next__name">{t(`prayer.${result.next.name}` as const)}</p>
                    <p className="next__time">{formatTime(result.next.at)}</p>
                    <p className="next__in">{formatCountdown(result.next.inMs, t)}</p>
                </div>
            )}

            {result?.highLatitude && (
                <p className="prayer__warn">{t('prayer.highLatitude')}</p>
            )}

            <ol className="times">
                {PRAYERS.map((p: PrayerName) => (
                    <li key={p} className={`times__row ${result?.next?.name === p ? 'is-next' : ''}`}>
                        <span className="times__name">{t(`prayer.${p}` as const)}</span>
                        <span className="times__at">{formatTime(result?.times[p] ?? null)}</span>
                    </li>
                ))}
                <li className="times__row times__row--muted">
                    <span className="times__name">{t('prayer.sunrise')}</span>
                    <span className="times__at">{formatTime(result?.times.sunrise ?? null)}</span>
                </li>
            </ol>

            <section className="qibla" aria-labelledby="qibla-h">
                <h2 id="qibla-h" className="qibla__heading">{t('qibla.title')}</h2>
                <div className={`qibla__dial ${live ? 'is-live' : ''}`} role="img"
                     aria-label={t('qibla.aria', { degrees: Math.round(bearing ?? 0) })}>
                    <div className="qibla__ring">
                        {['N', 'E', 'S', 'W'].map((d, i) => (
                            <span key={d} className="qibla__cardinal"
                                  style={{ transform: `rotate(${i * 90}deg) translateY(-104px) rotate(${-i * 90}deg)` }}>{d}</span>
                        ))}
                    </div>
                    <div className="qibla__needle" style={{ transform: `rotate(${needle}deg)` }}>
                        <span className="qibla__needle-head" aria-hidden="true" />
                    </div>
                    <div className="qibla__readout">
                        <strong>{Math.round(bearing ?? 0)}°</strong>
                        <span>{t('qibla.fromNorth')}</span>
                    </div>
                </div>

                {!live && (
                    <div className="qibla__note">
                        {compass === 'idle' || compass === 'denied' ? (
                            <>
                                <p>{t('qibla.needsPermission')}</p>
                                <button type="button" className="btn btn--primary" onClick={requestCompass}>{t('qibla.usecompass')}</button>
                            </>
                        ) : (
                            <p>{t('qibla.noCompass', { degrees: Math.round(bearing ?? 0) })}</p>
                        )}
                    </div>
                )}
            </section>

            <details className="prayer__settings">
                <summary>{t('prayer.method')}</summary>
                <div className="prayer__fields">
                    <label>
                        <span>{t('prayer.methodLabel')}</span>
                        <select value={methodId} onChange={(e) => setMethodId(e.target.value)}>
                            {METHODS.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    </label>
                    <label>
                        <span>{t('prayer.asrLabel')}</span>
                        <select value={madhab} onChange={(e) => setMadhab(e.target.value as Madhab)}>
                            <option value="standard">{t('prayer.asrStandard')}</option>
                            <option value="hanafi">{t('prayer.asrHanafi')}</option>
                        </select>
                    </label>
                </div>
                <p className="prayer__calc-note">{t('prayer.calcNote')}</p>
            </details>
        </div>
    );
}
